<template>
  <v-main
    ref="authShell"
    class="pa-0 auth-shell"
    :class="{ 'auth-shell--dark': isDarkTheme }"
  >
    <v-row no-gutters class="fill-height flex-nowrap auth-row-full-height">
      <v-col cols="12" lg="6" class="pa-0 d-none d-lg-block auth-col-branding">
        <HospitalBrandingPanel
          :hospital-config="hospitalConfig"
          :current-view="currentView"
          :hide-help="mostrarSinModulos"
        />
      </v-col>

      <v-col cols="12" lg="6" class="auth-col-form d-flex flex-column pa-0">
        <!-- Encabezado móvil (maqueta) -->
        <div
          class="auth-mobile-header d-flex d-lg-none flex-column align-center justify-center pa-6 text-white"
        >
          <div v-if="mobileLogoUrl" class="auth-mobile-logo mb-3">
            <img
              v-if="mobileLogoUseNativeImg"
              :src="mobileLogoUrl"
              alt=""
              class="auth-mobile-logo__img"
            />
            <v-img
              v-else
              :src="mobileLogoUrl"
              alt=""
              max-height="96"
              max-width="300"
              contain
            />
          </div>
          <v-avatar v-else size="64" class="auth-mobile-avatar mb-3">
            <span class="text-h5 font-weight-bold">{{ hospitalInitial }}</span>
          </v-avatar>
          <template v-if="!mobileLogoUrl">
            <h1 class="text-subtitle-1 font-weight-semibold text-center mb-1">
              {{ hospitalConfig.name }}
            </h1>
          </template>
        </div>

        <div
          class="auth-form-body flex-grow-1 d-flex align-center justify-center pa-6 pt-sm-12"
        >
          <v-sheet
            v-if="mostrarSinModulos"
            class="site-unavailable-card pa-6 text-center rounded-lg"
            elevation="0"
          >
            <v-icon
              class="site-unavailable-card__icon mb-3"
              :icon="siteUnavailableIcon"
              size="40"
              aria-hidden="true"
            />
            <h2 class="site-unavailable-card__title text-h6 font-weight-semibold mb-2">
              Sitio no disponible
            </h2>
            <p class="site-unavailable-card__text text-body-2 mb-0">
              El sitio no se encuentra disponible en este momento.<br />
              Para más información, comuníquese con la institución.
            </p>
          </v-sheet>
          <AutogestionarTurnoForm
            v-else-if="mostrarSoloAutogestion"
            :hospital-config="hospitalConfig"
            hide-navigation-actions
            @back="currentView = 'autogestionar-turno'"
          />
          <LoginForm
            v-else-if="currentView === 'login' && mostrarPortalForms"
            :hospital-config="hospitalConfig"
            @forgot-password="currentView = 'forgot-password'"
            @register="openRegister"
            @autogestionar-turno="currentView = 'autogestionar-turno'"
          />
          <RegisterForm
            v-else-if="currentView === 'register' && mostrarPortalForms"
            :hospital-config="hospitalConfig"
            :initial-td-id="registerInitialTdId"
            :initial-nro-doc="registerInitialNroDoc"
            @back="currentView = 'login'"
          />
          <ForgotPasswordForm
            v-else-if="currentView === 'forgot-password' && mostrarPortalForms"
            :hospital-config="hospitalConfig"
            @back="currentView = 'login'"
          />
          <AutogestionarTurnoForm
            v-else-if="currentView === 'autogestionar-turno' && autogestionImplementada"
            :hospital-config="hospitalConfig"
            @back="currentView = 'login'"
          />
        </div>

        <v-divider class="auth-footer-divider" />
        <div class="auth-footer pa-4 text-center text-caption">
          <p class="mb-0 d-flex align-center justify-center flex-wrap ga-2">
            <span class="macena-footer-lead">Un producto de</span>
            <a
              href="https://www.macena.com.ar/"
              target="_blank"
              rel="noopener noreferrer"
              class="macena-footer-link d-inline-flex align-center"
            >
              <img :src="macenaLogoSrc" alt="Macena" />
            </a>
          </p>
        </div>
      </v-col>
    </v-row>
  </v-main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import {
  resolveHospitalLogoUrl,
  hospitalLogoUsesNativeImg,
} from "./utils/helpers/hospitalLogoUrl";
import HospitalBrandingPanel from "./components/auth/HospitalBrandingPanel.vue";
import LoginForm from "./components/auth/LoginForm.vue";
import RegisterForm from "./components/auth/RegisterForm.vue";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm.vue";
import AutogestionarTurnoForm from "./components/auth/AutogestionarTurnoForm.vue";
import enums from "./utils/enums";
import macenaLogo from "./assets/img/macena_gray.svg";
import { derivePortalThemeColors } from "./utils/helpers/portalColorTheme";

export default {
  name: "PortalLoginView",
  components: {
    HospitalBrandingPanel,
    LoginForm,
    RegisterForm,
    ForgotPasswordForm,
    AutogestionarTurnoForm,
  },
  data() {
    return {
      currentView: "login",
      registerInitialTdId: null,
      registerInitialNroDoc: "",
      macenaLogoSrc: macenaLogo,
      siteUnavailableIcon: enums.icons.ALERT_CIRCLE,
    };
  },
  computed: {
    ...mapState("portalConfig", ["hospitalConfig"]),
    hospitalInitial() {
      return (this.hospitalConfig.name || "?").charAt(0).toUpperCase();
    },
    mobileLogoUrl() {
      return resolveHospitalLogoUrl(this.hospitalConfig.logoUrl);
    },
    mobileLogoUseNativeImg() {
      return hospitalLogoUsesNativeImg(this.mobileLogoUrl);
    },
    portalPacienteImplementado() {
      return this.hospitalConfig?.portalPacienteImplementado === true;
    },
    autogestionImplementada() {
      return this.hospitalConfig?.autogestionImplementada === true;
    },
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name;
      const value =
        themeName && typeof themeName === "object" ? themeName.value : themeName;
      return value === "dark";
    },
    portalThemeColors() {
      return derivePortalThemeColors(
        this.hospitalConfig.primaryColor || "#00838F",
        this.hospitalConfig.secondaryColor || "#E0F2F1",
        this.isDarkTheme
      );
    },
    mostrarPortalForms() {
      return this.portalPacienteImplementado;
    },
    mostrarSoloAutogestion() {
      return !this.portalPacienteImplementado && this.autogestionImplementada;
    },
    mostrarSinModulos() {
      return !this.portalPacienteImplementado && !this.autogestionImplementada;
    },
    menuTitle() {
      if (this.mostrarSoloAutogestion) return "Sacar turno";
      if (this.mostrarSinModulos) return "Portal no disponible";
      const titles = {
        login: "Inicio de sesión",
        register: "Registro",
        "forgot-password": "Restablecer contraseña",
        "autogestionar-turno": "Sacar turno",
      };
      return titles[this.currentView] || "Inicio de sesión";
    },
  },
  watch: {
    "$route.query.nuevoTurno": {
      handler() {
        this.applyNuevoTurnoLoginQuery();
      },
    },
    hospitalConfig: {
      handler() {
        this.applyPortalPrimaryVar();
        this.normalizeCurrentView();
      },
      deep: true,
    },
    menuTitle: {
      immediate: true,
      handler() {
        document.title = `${this.menuTitle} · Portal pacientes`;
      },
    },
  },
  mounted() {
    this.applyPortalPrimaryVar();
    this.normalizeCurrentView();
    this.applyNuevoTurnoLoginQuery();
  },
  methods: {
    ...mapActions({
      setAlert: "alerts/setAlert",
    }),
    openRegister(payload = {}) {
      this.registerInitialTdId = payload?.tdId ?? null;
      this.registerInitialNroDoc = String(payload?.nroDoc ?? "").trim();
      this.currentView = "register";
    },
    normalizeCurrentView() {
      if (this.mostrarSoloAutogestion) {
        this.currentView = "autogestionar-turno";
        return;
      }
      if (this.mostrarSinModulos) {
        this.currentView = "login";
        return;
      }
      if (
        !this.autogestionImplementada
        && this.currentView === "autogestionar-turno"
      ) {
        this.currentView = "login";
      }
      if (
        !this.portalPacienteImplementado
        && ["login", "register", "forgot-password"].includes(this.currentView)
      ) {
        this.currentView = this.autogestionImplementada
          ? "autogestionar-turno"
          : "login";
      }
    },
    applyNuevoTurnoLoginQuery() {
      const motivo = String(this.$route.query?.nuevoTurno ?? "").trim();
      if (!motivo) return;

      if (!this.autogestionImplementada) {
        this.setAlert({
          type: "warning",
          message: "La solicitud de turnos por documento no se encuentra disponible.",
        });
        const restQuery = { ...this.$route.query };
        delete restQuery.nuevoTurno;
        delete restQuery.mensaje;
        this.$router.replace({ name: "login", query: restQuery });
        return;
      }

      this.currentView = "autogestionar-turno";

      const mensajes = {
        "sesion-requerida":
          "Para sacar un turno debe ingresar su tipo y número de documento.",
        "sesion-expirada":
          "Su sesión ha expirado. Ingrese su documento nuevamente para solicitar un turno.",
        "error-validacion":
          "No se pudo validar si puede solicitar un nuevo turno. Intente nuevamente.",
      };

      let message = mensajes[motivo] ?? "";
      if (motivo === "limite-paciente") {
        const custom = String(this.$route.query?.mensaje ?? "").trim();
        message =
          custom
          || "No puede solicitar un nuevo turno en este momento.";
      }

      if (message) {
        this.setAlert({
          type: motivo === "error-validacion" ? "error" : "warning",
          message,
        });
      }

      if (this.$route.query?.nuevoTurno) {
        const restQuery = { ...this.$route.query };
        delete restQuery.nuevoTurno;
        delete restQuery.mensaje;
        this.$router.replace({ name: "login", query: restQuery });
      }
    },
    applyPortalPrimaryVar() {
      const el = this.$refs.authShell?.$el;
      if (el?.style) {
        const colors = this.portalThemeColors;
        el.style.setProperty("--portal-primary", colors.primary);
        el.style.setProperty("--portal-secondary", colors.secondary);
        el.style.setProperty("--portal-surface", colors.surface);
        el.style.setProperty("--portal-background", colors.background);
      }
    },
  },
};
</script>

<style scoped>
.auth-shell {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* Fallback hasta que aplique applyPortalPrimaryVar */
  --portal-primary: #00838f;
  --portal-secondary: #e0f2f1;
  --portal-surface: #ffffff;
  --portal-background: #f7f9fb;
}
.auth-row-full-height {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
}
.auth-mobile-header {
  background-color: var(--portal-primary);
}
.auth-mobile-logo {
  max-width: min(320px, 92vw);
  padding: 0;
  border-radius: 0;
  background: transparent;
}
.auth-mobile-logo :deep(img) {
  object-fit: contain;
}
.auth-mobile-logo__img {
  display: block;
  max-height: 96px;
  max-width: min(300px, 92vw);
  width: auto;
  height: auto;
  object-fit: contain;
}
.auth-mobile-avatar {
  background: rgba(255, 255, 255, 0.2);
}
.macena-footer-lead {
  color: #707070;
  font-size: 14px;
}
.macena-footer-link {
  line-height: 0;
}
.macena-footer-link img {
  display: block;
  height: 13px;
  width: auto;
}
.auth-col-branding {
  min-height: 0;
  height: 100%;
}
.auth-col-form {
  min-height: 0;
  max-height: 100%;
}
.auth-shell--dark .auth-col-form,
.auth-shell--dark .auth-form-body,
.auth-shell--dark .auth-footer {
  background: rgb(var(--v-theme-background));
}
.auth-form-body {
  min-height: 0;
  overflow-y: auto;
}
.site-unavailable-card {
  max-width: 28rem;
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}
.auth-shell--dark .site-unavailable-card {
  background-color: rgba(var(--v-theme-primary), 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.site-unavailable-card__icon,
.site-unavailable-card__title,
.site-unavailable-card__text {
  color: var(--portal-primary);
}
.auth-footer-divider {
  flex-shrink: 0;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.auth-shell--dark .auth-footer-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

@media (max-width: 1279px) {
  .auth-shell {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }

  .auth-row-full-height {
    height: auto;
    max-height: none;
    min-height: 100vh;
    min-height: 100dvh;
  }

  .auth-col-form {
    max-height: none;
    min-height: 100vh;
    min-height: 100dvh;
  }

  .auth-form-body {
    align-items: center !important;
    overflow-y: visible;
  }
}
</style>
